import {
  REGULAR,
  BOLD,
  SEMIBOLD,
  LIGHT,
  JETBRAINS_MONO_REGULAR,
  JETBRAINS_MONO_BOLD,
} from 'theme/fonts';
import type { ITYPOGRAPHY } from 'theme/types/typography';

// Open Sans Typography Sizes
const heading1 = { fontSize: '40px', lineHeight: '52px' };
const heading2 = { fontSize: '32px', lineHeight: '40px' };
const heading3 = { fontSize: '24px', lineHeight: '32px' };
const heading4 = { fontSize: '20px', lineHeight: '28px' };
const heading5 = { fontSize: '16px', lineHeight: '24px' };
const body1 = { fontSize: '18px', lineHeight: '20px' };
const body2 = { fontSize: '14px', lineHeight: '20px' };
const caption1 = { fontSize: '12px', lineHeight: '16px' };
const footnote = { fontSize: '10px', lineHeight: '12px' };

// JetBrains Mono Typography Sizes
const jetbrainsHeading1 = { fontSize: '40px', lineHeight: '52px' };
const jetbrainsHeading2 = { fontSize: '32px', lineHeight: '40px' };
const jetbrainsHeading3 = { fontSize: '24px', lineHeight: '32px' };
const jetbrainsHeading4 = { fontSize: '20px', lineHeight: '28px' };
const jetbrainsHeading5 = { fontSize: '16px', lineHeight: '24px' };
const jetbrainsBody1 = { fontSize: '18px', lineHeight: '20px' };
const jetbrainsBody2 = { fontSize: '14px', lineHeight: '20px' };
const jetbrainsCaption1 = { fontSize: '12px', lineHeight: '16px' };
const jetbrainsFootnote = { fontSize: '10px', lineHeight: '12px' };
// Legacy alias
const jetbrainsBody = jetbrainsBody2;

const typography: ITYPOGRAPHY = {
  fontFamily: `${REGULAR}, sans-serif`,

  // Heading 1 - 40px, 52px line height
  h1_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...heading1,
  },
  h1_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...heading1,
  },

  // Heading 2 - 32px, 40px line height
  h2_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...heading2,
  },
  h2_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...heading2,
  },

  // Heading 3 - 24px, 32px line height
  h3_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...heading3,
  },
  h3_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...heading3,
  },

  // Heading 4 - 20px, 28px line height
  h4_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...heading4,
  },
  h4_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...heading4,
  },

  // Heading 5 - 16px, 24px line height
  h5_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...heading5,
  },
  h5_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...heading5,
  },
  h5_Light: {
    fontFamily: `${LIGHT}, sans-serif`,
    fontWeight: 300,
    ...heading5,
  },

  // Body - 14px, 20px line height
  body1_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...body1,
  },
  body1_Semibold: {
    fontFamily: `${SEMIBOLD}, sans-serif`,
    fontWeight: 600,
    ...body1,
  },

  body1_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...body1,
  },
  body1_Light: {
    fontFamily: `${LIGHT}, sans-serif`,
    fontWeight: 300,
    ...body1,
  },

  // Body 2 - 14px, 20px line height
  body2_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...body2,
  },
  body2_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...body2,
  },
  body2_Light: {
    fontFamily: `${LIGHT}, sans-serif`,
    fontWeight: 300,
    ...body2,
  },

  // Caption - 12px, 16px line height
  caption1_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...caption1,
  },
  caption1_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...caption1,
  },
  caption1_Light: {
    fontFamily: `${LIGHT}, sans-serif`,
    fontWeight: 300,
    ...caption1,
  },

  // Footnote - 10px, 12px line height
  footnote_Bold: {
    fontFamily: `${BOLD}, sans-serif`,
    fontWeight: 700,
    ...footnote,
  },
  footnote_Regular: {
    fontFamily: `${REGULAR}, sans-serif`,
    fontWeight: 400,
    ...footnote,
  },
  footnote_Light: {
    fontFamily: `${LIGHT}, sans-serif`,
    fontWeight: 300,
    ...footnote,
  },

  // JetBrains Mono - Heading 1 - 40px, 52px line height
  h1_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsHeading1,
  },
  h1_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsHeading1,
  },

  // JetBrains Mono - Heading 2 - 32px, 40px line height
  h2_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsHeading2,
  },
  h2_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsHeading2,
  },

  // JetBrains Mono - Heading 3 - 24px, 32px line height
  h3_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsHeading3,
  },
  h3_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsHeading3,
  },

  // JetBrains Mono - Heading 4 - 20px, 28px line height
  h4_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsHeading4,
  },
  h4_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsHeading4,
  },

  // JetBrains Mono - Heading 5 - 16px, 24px line height
  h5_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsHeading5,
  },
  h5_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsHeading5,
  },
  h5_JetBrains_Light: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 300,
    ...jetbrainsHeading5,
  },

  // JetBrains Mono - Body 1 - 18px, 20px line height
  body1_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsBody1,
  },
  body1_JetBrains_Semibold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 600,
    ...jetbrainsBody1,
  },
  body1_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsBody1,
  },
  body1_JetBrains_Light: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 300,
    ...jetbrainsBody1,
  },

  // JetBrains Mono - Body 2 - 14px, 20px line height
  body2_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsBody2,
  },
  body2_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsBody2,
  },
  body2_JetBrains_Light: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 300,
    ...jetbrainsBody2,
  },

  // JetBrains Mono - Caption - 12px, 16px line height
  caption1_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsCaption1,
  },
  caption1_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsCaption1,
  },
  caption1_JetBrains_Light: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 300,
    ...jetbrainsCaption1,
  },

  // JetBrains Mono - Footnote - 10px, 12px line height
  footnote_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsFootnote,
  },
  footnote_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsFootnote,
  },
  footnote_JetBrains_Light: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 300,
    ...jetbrainsFootnote,
  },

  // Legacy aliases kept for compatibility
  body_JetBrains_Regular: {
    fontFamily: `${JETBRAINS_MONO_REGULAR}, monospace`,
    fontWeight: 400,
    ...jetbrainsBody,
  },
  body_JetBrains_Bold: {
    fontFamily: `${JETBRAINS_MONO_BOLD}, monospace`,
    fontWeight: 700,
    ...jetbrainsBody,
  },

  // Default MUI variants (keeping for compatibility)
  h1: {
    fontSize: '40px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '52px',
    fontWeight: 400,
  },
  h2: {
    fontSize: '32px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '40px',
    fontWeight: 400,
  },
  h3: {
    fontSize: '24px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '32px',
    fontWeight: 400,
  },
  h4: {
    fontSize: '20px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '28px',
    fontWeight: 400,
  },
  h5: {
    fontSize: '16px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '24px',
    fontWeight: 400,
  },
  h6: {
    fontSize: '16px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '24px',
    fontWeight: 400,
  },
  body1: {
    fontSize: '14px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '20px',
    fontWeight: 400,
  },
  body2: {
    fontSize: '14px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '20px',
    fontWeight: 400,
  },
  button: {
    fontSize: '14px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '20px',
    fontWeight: 400,
    textTransform: 'none',
  },
  caption: {
    fontSize: '12px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '16px',
    fontWeight: 400,
  },
  overline: {
    fontSize: '10px',
    fontFamily: `${REGULAR}, sans-serif`,
    lineHeight: '12px',
    fontWeight: 400,
    textTransform: 'uppercase',
  },
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
};

export default typography;
